import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Bar, Doughnut, Line } from "react-chartjs-2";
import { listMeetingStatisticsData, setStatisticsPeriod } from "../statisticsSlice";
import {
  selectStatisticsError,
  selectStatisticsLoading,
  selectStatisticsMeetings,
  selectStatisticsMessage,
  selectStatisticsPeriod,
} from "../statisticsSelectors";
import { STATISTICS_PERIODS } from "../statistics.types";

const DAILY_BUCKET_COUNT = 24;
const WEEKLY_BUCKET_COUNT = 7;
const MONTHLY_BUCKET_COUNT = 30;
const YEARLY_BUCKET_COUNT = 12;

const asMillis = (value) => {
  if (!value) return null;
  if (typeof value === "number") return value;
  return null;
};

const buildBuckets = (period, language) => {
  const now = new Date();

  if (period === STATISTICS_PERIODS.DAILY) {
    const buckets = [];
    const current = new Date(now);
    current.setMinutes(0, 0, 0);

    for (let i = DAILY_BUCKET_COUNT - 1; i >= 0; i -= 1) {
      const start = new Date(current);
      start.setHours(current.getHours() - i);
      const end = new Date(start);
      end.setHours(start.getHours() + 1);
      buckets.push({
        label: start.toLocaleTimeString(language === "tr" ? "tr-TR" : "en-US", {
          hour: "2-digit",
          minute: "2-digit",
        }),
        start: start.getTime(),
        end: end.getTime(),
      });
    }

    return buckets;
  }

  if (period === STATISTICS_PERIODS.WEEKLY || period === STATISTICS_PERIODS.MONTHLY) {
    const bucketCount = period === STATISTICS_PERIODS.WEEKLY ? WEEKLY_BUCKET_COUNT : MONTHLY_BUCKET_COUNT;
    const buckets = [];
    const current = new Date(now);
    current.setHours(0, 0, 0, 0);

    for (let i = bucketCount - 1; i >= 0; i -= 1) {
      const start = new Date(current);
      start.setDate(current.getDate() - i);
      const end = new Date(start);
      end.setDate(start.getDate() + 1);
      buckets.push({
        label: start.toLocaleDateString(language === "tr" ? "tr-TR" : "en-US", {
          day: "2-digit",
          month: "2-digit",
        }),
        start: start.getTime(),
        end: end.getTime(),
      });
    }

    return buckets;
  }

  const buckets = [];
  const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);

  for (let i = YEARLY_BUCKET_COUNT - 1; i >= 0; i -= 1) {
    const start = new Date(firstOfMonth.getFullYear(), firstOfMonth.getMonth() - i, 1, 0, 0, 0, 0);
    const end = new Date(start.getFullYear(), start.getMonth() + 1, 1, 0, 0, 0, 0);
    buckets.push({
      label: start.toLocaleDateString(language === "tr" ? "tr-TR" : "en-US", {
        month: "short",
        year: "2-digit",
      }),
      start: start.getTime(),
      end: end.getTime(),
    });
  }

  return buckets;
};

const countByBuckets = (items, buckets) => {
  return buckets.map((bucket) => {
    return items.reduce((acc, item) => {
      const ts = asMillis(item?.createdAt);
      if (!ts) return acc;
      if (ts < bucket.start || ts >= bucket.end) return acc;
      return acc + 1;
    }, 0);
  });
};

const inRange = (ts, start, end) => Number.isFinite(ts) && ts >= start && ts < end;

const MeetingStatisticsPage = () => {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const meetings = useSelector(selectStatisticsMeetings);
  const isLoading = useSelector(selectStatisticsLoading);
  const isError = useSelector(selectStatisticsError);
  const message = useSelector(selectStatisticsMessage);
  const selectedPeriod = useSelector(selectStatisticsPeriod);

  useEffect(() => {
    dispatch(listMeetingStatisticsData());
  }, [dispatch]);

  const periodOptions = useMemo(
    () => [
      { key: STATISTICS_PERIODS.DAILY, label: t("statistics.period.daily") },
      { key: STATISTICS_PERIODS.WEEKLY, label: t("statistics.period.weekly") },
      { key: STATISTICS_PERIODS.MONTHLY, label: t("statistics.period.monthly") },
      { key: STATISTICS_PERIODS.YEARLY, label: t("statistics.period.yearly") },
    ],
    [t]
  );

  const buckets = useMemo(() => buildBuckets(selectedPeriod, i18n.language), [selectedPeriod, i18n.language]);
  const rangeStart = buckets[0]?.start || 0;
  const rangeEnd = buckets[buckets.length - 1]?.end || Date.now();

  const filteredMeetings = useMemo(
    () => meetings.filter((item) => inRange(asMillis(item?.createdAt), rangeStart, rangeEnd)),
    [meetings, rangeStart, rangeEnd]
  );

  const meetingsSeries = useMemo(() => countByBuckets(meetings, buckets), [meetings, buckets]);

  const sumBy = (items, selector) =>
    items.reduce((acc, item) => acc + (Number(selector(item)) || 0), 0);

  const totalMeetings = meetings.length;
  const selectedMeetings = filteredMeetings.length;

  const selectedPrivate = filteredMeetings.filter((item) => item?.isPrivate).length;
  const totalPrivate = meetings.filter((item) => item?.isPrivate).length;
  const selectedPremium = filteredMeetings.filter((item) => item?.isPremium).length;
  const totalPremium = meetings.filter((item) => item?.isPremium).length;
  const selectedBusinessState = filteredMeetings.filter((item) => item?.businessState).length;
  const totalBusinessState = meetings.filter((item) => item?.businessState).length;

  const selectedAdmins = sumBy(filteredMeetings, (item) => item?.adminUsersCount);
  const totalAdmins = sumBy(meetings, (item) => item?.adminUsersCount);
  const selectedModerators = sumBy(filteredMeetings, (item) => item?.moderatorUsersCount);
  const totalModerators = sumBy(meetings, (item) => item?.moderatorUsersCount);
  const selectedBtob = sumBy(filteredMeetings, (item) => item?.btobMeetingsCount);
  const totalBtob = sumBy(meetings, (item) => item?.btobMeetingsCount);
  const selectedMatchingSurvey = sumBy(filteredMeetings, (item) => item?.matchingSurveyUsersCount);
  const totalMatchingSurvey = sumBy(meetings, (item) => item?.matchingSurveyUsersCount);
  const selectedSessions = sumBy(filteredMeetings, (item) => item?.sessionCount);
  const totalSessions = sumBy(meetings, (item) => item?.sessionCount);
  const selectedSessionDesks = sumBy(filteredMeetings, (item) => item?.sessionDeskCount);
  const totalSessionDesks = sumBy(meetings, (item) => item?.sessionDeskCount);
  const selectedSessionBreak = sumBy(filteredMeetings, (item) => item?.sessionBreakDuration);
  const totalSessionBreak = sumBy(meetings, (item) => item?.sessionBreakDuration);
  const selectedAmount = sumBy(filteredMeetings, (item) => item?.amount);
  const totalAmount = sumBy(meetings, (item) => item?.amount);

  const lineData = {
    labels: buckets.map((bucket) => bucket.label),
    datasets: [
      {
        label: t("statistics.metrics.meetings"),
        data: meetingsSeries,
        borderColor: "#1e88e5",
        backgroundColor: "rgba(30, 136, 229, 0.15)",
        tension: 0.3,
      },
    ],
  };

  const doughnutData = {
    labels: [
      t("statistics.metrics.private"),
      t("statistics.metrics.public"),
      t("statistics.metrics.premium"),
      t("statistics.metrics.standard"),
      t("statistics.metrics.business_state"),
      t("statistics.metrics.non_business_state"),
    ],
    datasets: [
      {
        data: [
          selectedPrivate,
          Math.max(selectedMeetings - selectedPrivate, 0),
          selectedPremium,
          Math.max(selectedMeetings - selectedPremium, 0),
          selectedBusinessState,
          Math.max(selectedMeetings - selectedBusinessState, 0),
        ],
        backgroundColor: ["#42a5f5", "#cfd8dc", "#7e57c2", "#e0e0e0", "#26a69a", "#ffcc80"],
        borderWidth: 1,
      },
    ],
  };

  const barData = {
    labels: [
      t("statistics.metrics.admin_users"),
      t("statistics.metrics.moderator_users"),
      t("statistics.metrics.btob_meetings"),
      t("statistics.metrics.matching_survey_users"),
      t("statistics.metrics.session_count"),
      t("statistics.metrics.session_desk_count"),
    ],
    datasets: [
      {
        label: t("statistics.selected_period"),
        data: [
          selectedAdmins,
          selectedModerators,
          selectedBtob,
          selectedMatchingSurvey,
          selectedSessions,
          selectedSessionDesks,
        ],
        backgroundColor: ["#1e88e5", "#43a047", "#fb8c00", "#5e35b1", "#00897b", "#6d4c41"],
      },
    ],
  };

  const locale = i18n.language === "tr" ? "tr-TR" : "en-US";
  const formatNumber = (value) => Number(value || 0).toLocaleString(locale);
  const latestMeetings = useMemo(() => {
    return [...filteredMeetings]
      .sort((a, b) => (asMillis(b?.createdAt) || 0) - (asMillis(a?.createdAt) || 0))
      .slice(0, 8);
  }, [filteredMeetings]);

  return (
    <div className="row">
      <div className="col-12">
        <div className="card">
          <div className="card-body">
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
              <h4 className="card-title mb-0">{t("statistics.meeting_title")}</h4>
              <div className="btn-group">
                {periodOptions.map((option) => (
                  <button
                    key={option.key}
                    type="button"
                    className={`btn ${selectedPeriod === option.key ? "btn-primary" : "btn-outline-primary"}`}
                    onClick={() => dispatch(setStatisticsPeriod(option.key))}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => dispatch(listMeetingStatisticsData({ force: true }))}
                disabled={isLoading}
              >
                {t("statistics.refresh")}
              </button>
            </div>

            {isError ? (
              <div className="alert alert-danger mb-0">{message || t("statistics.load_error")}</div>
            ) : (
              <>
                <div className="row row-cols-xl-5 row-cols-lg-4 row-cols-md-3 row-cols-sm-2 g-3 mb-4">
                  <div className="col">
                    <div className="card h-100 mb-0">
                      <div className="card-body py-3">
                        <h6 className="mb-1">{t("statistics.metrics.meetings")}</h6>
                        <h4 className="mb-0">{formatNumber(selectedMeetings)}</h4>
                        <small className="text-muted">{t("statistics.total")}: {formatNumber(totalMeetings)}</small>
                      </div>
                    </div>
                  </div>
                  <div className="col">
                    <div className="card h-100 mb-0">
                      <div className="card-body py-3">
                        <h6 className="mb-1">{t("statistics.metrics.private")}</h6>
                        <h4 className="mb-0">{formatNumber(selectedPrivate)}</h4>
                        <small className="text-muted">{t("statistics.total")}: {formatNumber(totalPrivate)}</small>
                      </div>
                    </div>
                  </div>
                  <div className="col">
                    <div className="card h-100 mb-0">
                      <div className="card-body py-3">
                        <h6 className="mb-1">{t("statistics.metrics.premium")}</h6>
                        <h4 className="mb-0">{formatNumber(selectedPremium)}</h4>
                        <small className="text-muted">{t("statistics.total")}: {formatNumber(totalPremium)}</small>
                      </div>
                    </div>
                  </div>
                  <div className="col">
                    <div className="card h-100 mb-0">
                      <div className="card-body py-3">
                        <h6 className="mb-1">{t("statistics.metrics.business_state")}</h6>
                        <h4 className="mb-0">{formatNumber(selectedBusinessState)}</h4>
                        <small className="text-muted">{t("statistics.total")}: {formatNumber(totalBusinessState)}</small>
                      </div>
                    </div>
                  </div>
                  <div className="col">
                    <div className="card h-100 mb-0">
                      <div className="card-body py-3">
                        <h6 className="mb-1">{t("statistics.metrics.admin_users")}</h6>
                        <h4 className="mb-0">{formatNumber(selectedAdmins)}</h4>
                        <small className="text-muted">{t("statistics.total")}: {formatNumber(totalAdmins)}</small>
                      </div>
                    </div>
                  </div>
                  <div className="col">
                    <div className="card h-100 mb-0">
                      <div className="card-body py-3">
                        <h6 className="mb-1">{t("statistics.metrics.moderator_users")}</h6>
                        <h4 className="mb-0">{formatNumber(selectedModerators)}</h4>
                        <small className="text-muted">{t("statistics.total")}: {formatNumber(totalModerators)}</small>
                      </div>
                    </div>
                  </div>
                  <div className="col">
                    <div className="card h-100 mb-0">
                      <div className="card-body py-3">
                        <h6 className="mb-1">{t("statistics.metrics.btob_meetings")}</h6>
                        <h4 className="mb-0">{formatNumber(selectedBtob)}</h4>
                        <small className="text-muted">{t("statistics.total")}: {formatNumber(totalBtob)}</small>
                      </div>
                    </div>
                  </div>
                  <div className="col">
                    <div className="card h-100 mb-0">
                      <div className="card-body py-3">
                        <h6 className="mb-1">{t("statistics.metrics.session_count")}</h6>
                        <h4 className="mb-0">{formatNumber(selectedSessions)}</h4>
                        <small className="text-muted">{t("statistics.total")}: {formatNumber(totalSessions)}</small>
                      </div>
                    </div>
                  </div>
                  <div className="col">
                    <div className="card h-100 mb-0">
                      <div className="card-body py-3">
                        <h6 className="mb-1">{t("statistics.metrics.amount")}</h6>
                        <h4 className="mb-0">{formatNumber(selectedAmount)}</h4>
                        <small className="text-muted">{t("statistics.total")}: {formatNumber(totalAmount)}</small>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="row g-3 mb-4">
                  <div className="col-lg-7">
                    <div className="card mb-0">
                      <div className="card-body">
                        <h5 className="card-title">{t("statistics.trend_title")}</h5>
                        <Line data={lineData} />
                      </div>
                    </div>
                  </div>
                  <div className="col-lg-5">
                    <div className="card mb-0">
                      <div className="card-body">
                        <h5 className="card-title">{t("statistics.meeting_distribution_title")}</h5>
                        <Doughnut data={doughnutData} />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="row g-3 mb-4">
                  <div className="col-12">
                    <div className="card mb-0">
                      <div className="card-body">
                        <h5 className="card-title">{t("statistics.meeting_volume_title")}</h5>
                        <Bar data={barData} />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="row g-3">
                  <div className="col-12">
                    <div className="card mb-0">
                      <div className="card-body">
                        <h5 className="card-title">{t("statistics.latest_meetings")}</h5>
                        <div className="table-responsive">
                          <table className="table table-striped mb-0">
                            <thead>
                              <tr>
                                <th>{t("statistics.columns.meeting_id")}</th>
                                <th>{t("statistics.columns.private")}</th>
                                <th>{t("statistics.columns.premium")}</th>
                                <th>{t("statistics.columns.business_state")}</th>
                                <th>{t("statistics.columns.admin_users")}</th>
                                <th>{t("statistics.columns.moderator_users")}</th>
                                <th>{t("statistics.columns.btob_meetings")}</th>
                                <th>{t("statistics.columns.session_count")}</th>
                                <th>{t("statistics.columns.amount")}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {latestMeetings.length === 0 ? (
                                <tr>
                                  <td colSpan="9" className="text-muted">
                                    {isLoading ? t("statistics.loading") : t("statistics.no_data")}
                                  </td>
                                </tr>
                              ) : (
                                latestMeetings.map((item) => (
                                  <tr key={item.id}>
                                    <td>{item.meetingId || item.id}</td>
                                    <td>{item.isPrivate ? t("statistics.yes") : t("statistics.no")}</td>
                                    <td>{item.isPremium ? t("statistics.yes") : t("statistics.no")}</td>
                                    <td>{item.businessState ? t("statistics.yes") : t("statistics.no")}</td>
                                    <td>{formatNumber(item.adminUsersCount)}</td>
                                    <td>{formatNumber(item.moderatorUsersCount)}</td>
                                    <td>{formatNumber(item.btobMeetingsCount)}</td>
                                    <td>{formatNumber(item.sessionCount)}</td>
                                    <td>{formatNumber(item.amount)}</td>
                                  </tr>
                                ))
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MeetingStatisticsPage;
